import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cloud, Loader2, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useFicStatus } from "@/context/FicStatusContext";

export default function FicSendModal({ open, onClose, invoiceId, onSent }) {
  const { status: globalStatus, refresh: refreshFic } = useFicStatus();
  const [dryRun, setDryRun] = useState(true);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [ficStatus, setFicStatus] = useState(null);

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setDryRun(true);
    // Prefer the freshly-fetched global status; fall back to a direct call
    if (globalStatus) {
      setFicStatus(globalStatus);
    } else {
      refreshFic().then((s) => setFicStatus(s));
    }
  }, [open, globalStatus, refreshFic]);

  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/fic/invoices/${invoiceId}/send`, { dry_run: dryRun });
      setResult(data);
      if (data.status === "sent" || data.status === "dry_run_ok") {
        toast.success(dryRun ? "Test dry-run riuscito!" : "Fattura inviata a Fatture in Cloud");
        onSent?.(data);
      } else {
        toast.error("Invio con errore: controlla il dettaglio");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio");
      setResult({ status: "error", send_error: e?.response?.data?.detail || String(e) });
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;
  const isReady = ficStatus?.connected && ficStatus?.selected_company_id;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-6" onClick={onClose} data-testid="fic-send-modal">
      <div className="bg-white rounded-xl max-w-lg w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-[#E8E4D9]">
          <div className="font-display font-semibold flex items-center gap-2">
            <Cloud className="w-4 h-4 text-[#1D4ED8]" /> Invia a Fatture in Cloud
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white" data-testid="fic-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {!ficStatus ? (
            <div className="grid place-items-center py-6"><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div>
          ) : !isReady ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="fic-not-ready">
              Devi prima collegare Fatture in Cloud e selezionare un&apos;azienda.{" "}
              <Link to="/app/integrations" className="font-medium text-[#1D4ED8] hover:underline">
                Vai alle integrazioni →
              </Link>
            </div>
          ) : (
            <>
              <div className="text-sm text-zinc-700">
                Verrà creata un&apos;autofattura equivalente nel tuo account Fatture in Cloud e inoltrata al SDI.
              </div>
              <div className="rounded-md border border-[#E8E4D9] bg-[#F5F2EA] p-3 text-xs text-zinc-700">
                <div>Azienda destinazione: <strong>{ficStatus.selected_company_name}</strong></div>
                <div className="font-mono">Company ID: {ficStatus.selected_company_id}</div>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={dryRun}
                  onChange={(e) => setDryRun(e.target.checked)}
                  data-testid="fic-dry-run-toggle"
                  className="w-4 h-4 accent-blue-600"
                />
                <span>
                  Modalità <strong>test (dry-run)</strong> — non invia realmente allo SDI
                </span>
              </label>
              {result && (
                <div className="rounded-md bg-zinc-950 text-emerald-300 p-3 text-[11px] font-mono max-h-64 overflow-auto" data-testid="fic-result">
                  <pre>{JSON.stringify(result, null, 2)}</pre>
                </div>
              )}
            </>
          )}
        </div>
        {isReady && (
          <div className="p-3 border-t border-[#E8E4D9] flex justify-end gap-2 bg-[#F5F2EA]">
            <button onClick={onClose} className="px-4 py-2 rounded-md text-sm text-zinc-700 hover:bg-white" data-testid="fic-cancel-btn">
              Annulla
            </button>
            <button
              onClick={send}
              disabled={sending}
              data-testid="fic-send-btn"
              className="inline-flex items-center gap-2 px-5 py-2 rounded-md bg-[#1D4ED8] text-white text-sm font-semibold hover:bg-[#1E3A8A] disabled:opacity-70 transition-colors"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Cloud className="w-4 h-4" />}
              {dryRun ? "Esegui test" : "Invia allo SDI"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
